import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSEO, addJsonLd } from '../../utils/seo';

const HomePage: React.FC = () => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState(1);
  const [quizData, setQuizData] = useState({
    role: '',
    projectType: '',
    area: '',
    budget: '',
    timeline: '',
    tileType: '',
    roomType: '',
    additionalInfo: ''
  });

  const [timeLeft, setTimeLeft] = useState({
    days: 3,
    hours: 12,
    minutes: 45,
    seconds: 30
  });

  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { days, hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleQuizSubmit = () => {
    const message = `Новая заявка с сайта MUSLIM CERAMICA:
    
👤 Роль: ${quizData.role}
🏗️ Тип проекта: ${quizData.projectType}
📐 Площадь: ${quizData.area}
🎨 Тип плитки: ${quizData.tileType}
🏠 Помещение: ${quizData.roomType}
💰 Бюджет: ${quizData.budget || 'Не указан'}
⏰ Сроки: ${quizData.timeline}
${quizData.additionalInfo ? `📋 Дополнительно: ${quizData.additionalInfo}` : ''}`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/77085773668?text=${encodedMessage}`, '_blank');
    setShowQuiz(false);
    setQuizStep(1);
    setQuizData({
      role: '',
      projectType: '',
      area: '',
      budget: '',
      timeline: '',
      tileType: '',
      roomType: '',
      additionalInfo: ''
    });
  };

  const openQuiz = () => {
    setShowQuiz(true);
    setQuizStep(1);
  };

  const openDirectWhatsApp = (message: string) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/77085773668?text=${encodedMessage}`, '_blank');
  };

  const nextStep = () => {
    if (quizStep < 7) {
      setQuizStep(quizStep + 1);
    } else {
      handleQuizSubmit();
    }
  };

  const prevStep = () => {
    if (quizStep > 1) {
      setQuizStep(quizStep - 1);
    }
  };

  const closeQuiz = () => {
    setShowQuiz(false);
    setQuizStep(1);
  };

  const getRoleBasedOptions = () => {
    const universalAreas = ['До 100 м²', 'До 500 м²', 'До 2 000 м²', 'До 5 000 м²', 'Свыше 5 000 м²'];
    
    switch (quizData.role) {
      case 'Частный клиент':
        return {
          projectTypes: ['Квартира', 'Частный дом', 'Дача/коттедж', 'Ванная комната', 'Кухня', 'Балкон/лоджия'],
          areas: universalAreas,
          budgets: ['От 50 000 тг', 'От 100 000 тг', 'От 200 000 тг', 'От 500 000 тг', 'От 1 млн тг', 'Более 2 млн тг']
        };
      case 'Застройщик':
        return {
          projectTypes: ['Жилой комплекс', 'Коттеджный поселок', 'Многоэтажный дом', 'Таунхаусы', 'Апартаменты'],
          areas: universalAreas,
          budgets: ['От 2 млн тг', 'От 5 млн тг', 'От 10 млн тг', 'От 25 млн тг', 'От 50 млн тг', 'Более 100 млн тг']
        };
      case 'Строительная компания':
        return {
          projectTypes: ['Коммерческий объект', 'Жилой комплекс', 'Офисное здание', 'Торговый центр', 'Промышленный объект', 'Социальный объект'],
          areas: universalAreas,
          budgets: ['От 1 млн тг', 'От 3 млн тг', 'От 5 млн тг', 'От 15 млн тг', 'От 30 млн тг', 'Более 50 млн тг']
        };
      case 'Дизайнер/Архитектор':
        return {
          projectTypes: ['Квартира премиум', 'Частный дом', 'Коммерческий интерьер', 'Ресторан/отель', 'Офис', 'Шоурум'],
          areas: universalAreas,
          budgets: ['От 100 000 тг', 'От 300 000 тг', 'От 800 000 тг', 'От 1,5 млн тг', 'От 3 млн тг', 'Более 5 млн тг']
        };
      case 'Дилер/Партнер':
        return {
          projectTypes: ['Розничные продажи', 'Мелкий опт', 'Крупный опт', 'Дистрибуция', 'Интернет-продажи'],
          areas: universalAreas,
          budgets: ['От 200 000 тг', 'От 500 000 тг', 'От 1 млн тг', 'От 3 млн тг', 'От 10 млн тг', 'Более 20 млн тг']
        };
      case 'Тендерный отдел':
        return {
          projectTypes: ['Госзаказ', 'Социальный объект', 'Образовательное учреждение', 'Медицинское учреждение', 'Спортивный объект'],
          areas: universalAreas,
          budgets: ['От 2 млн тг', 'От 5 млн тг', 'От 10 млн тг', 'От 20 млн тг', 'От 50 млн тг', 'Более 100 млн тг']
        };
      default:
        return {
          projectTypes: ['Квартира/дом', 'Коммерческий объект', 'Жилой комплекс', 'Другое'],
          areas: universalAreas,
          budgets: ['От 100 000 тг', 'От 500 000 тг', 'От 1 млн тг', 'От 3 млн тг', 'От 5 млн тг', 'Более 10 млн тг']
        };
    }
  };

  const faqData = [
    {
      question: "Какие форматы керамической плитки у вас есть?",
      answer: "У нас в наличии керамическая плитка форматов 60×60 см и 120×60 см. Матовые и глянцевые поверхности, более 60 различных дизайнов."
    },
    {
      question: "Сколько стоит керамическая плитка?",
      answer: "Цены зависят от формата и дизайна. Для получения актуальных цен свяжитесь с нами. Для крупных заказов действуют специальные условия."
    },
    {
      question: "Есть ли доставка по области?",
      answer: "Да, доставляем по всей Костанайской области: Костанай, Рудный, Лисаковск, Аркалык и все районы. Стоимость доставки рассчитывается индивидуально."
    },
    {
      question: "Можно ли посмотреть образцы?",
      answer: "Конечно! Приезжайте в наш шоурум по адресу ул. Карбышева 8/1 в Костанае. Работаем ежедневно с 9:00 до 19:00."
    },
    {
      question: "Какая гарантия на керамическую плитку?",
      answer: "Заводская гарантия на керамическую плитку составляет 25 лет. Мы также предоставляем гарантию на качество поставляемого товара."
    },
    {
      question: "Работаете ли с юридическими лицами?",
      answer: "Да, работаем как с физическими, так и с юридическими лицами. Предоставляем все необходимые документы, работаем с НДС."
    }
  ];

  useEffect(() => {
    // Update SEO
    updateSEO({
      title: 'MUSLIM CERAMICA - Керамическая плитка №1 в Костанае | Terramic, Wan SHENG, MinTao',
      description: 'Официальный представитель заводов Terramic, Wan SHENG, MinTao в Костанае. Более 25 000 м² керамической плитки в наличии. Розница и опт. Доставка по Костанайской области.',
      keywords: 'керамическая плитка Костанай, плитка Костанай, Terramic, Wan SHENG, MinTao, керамическая плитка, строительные материалы',
      canonical: '/'
    });

    // Add Organization Schema
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "MUSLIM CERAMICA",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
      "description": "Официальный представитель заводов керамической плитки Terramic, Wan SHENG, MinTao в Костанайской области",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "ул. Карбышева 8/1",
        "addressLocality": "Костанай",
        "addressCountry": "KZ"
      },
      "telephone": "+7 708 577-36-68",
      "email": "info@muslimceramica.kz",
      "foundingDate": "2017",
      "areaServed": "Костанайская область",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Каталог керамической плитки",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Керамическая плитка Terramic",
              "brand": "Terramic"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Керамическая плитка Wan SHENG",
              "brand": "Wan SHENG"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Керамическая плитка MinTao",
              "brand": "MinTao"
            }
          }
        ]
      }
    });

    // Add LocalBusiness Schema
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "MUSLIM CERAMICA",
      "image": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
      "telephone": "+7 708 577-36-68",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "ул. Карбышева 8/1",
        "addressLocality": "Костанай",
        "addressCountry": "KZ"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 53.2141,
        "longitude": 63.6246
      },
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday", 
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "09:00",
        "closes": "19:00"
      },
      "priceRange": "$$"
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Quiz Modal */}
      {showQuiz && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={closeQuiz}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-green-700">Подбор решения</h3>
                <span className="text-sm text-gray-500">{quizStep}/7</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(quizStep / 7) * 100}%` }}
                ></div>
              </div>
            </div>

            {quizStep === 1 && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Кто вы?</h4>
                <div className="space-y-2">
                  {[
                    { value: 'Частный клиент', icon: '🏠', desc: 'Ремонт квартиры, дома' },
                    { value: 'Застройщик', icon: '🏗️', desc: 'ЖК, коттеджные поселки' },
                    { value: 'Строительная компания', icon: '🔨', desc: 'Подрядные работы' },
                    { value: 'Дизайнер/Архитектор', icon: '🎨', desc: 'Дизайн-проекты' },
                    { value: 'Дилер/Партнер', icon: '🤝', desc: 'Перепродажа, партнерство' },
                    { value: 'Тендерный отдел', icon: '📋', desc: 'Госзакупки, тендеры' }
                  ].map((role) => (
                    <button
                      key={role.value}
                      onClick={() => setQuizData({...quizData, role: role.value})}
                      className={`w-full p-4 text-left border rounded-lg transition-colors ${
                        quizData.role === role.value 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{role.icon}</span>
                        <div>
                          <div className="font-semibold">{role.value}</div>
                          <div className="text-sm text-gray-500">{role.desc}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {quizStep === 2 && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Тип вашего проекта</h4>
                <div className="space-y-2">
                  {getRoleBasedOptions().projectTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setQuizData({...quizData, projectType: type})}
                      className={`w-full p-3 text-left border rounded-lg transition-colors ${
                        quizData.projectType === type 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {quizStep === 3 && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Площадь для облицовки</h4>
                <div className="space-y-2">
                  {getRoleBasedOptions().areas.map((area) => (
                    <button
                      key={area}
                      onClick={() => setQuizData({...quizData, area: area})}
                      className={`w-full p-3 text-left border rounded-lg transition-colors ${
                        quizData.area === area 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {quizStep === 4 && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Какой тип плитки интересует?</h4>
                <div className="space-y-2">
                  {[
                    { value: 'Под мрамор', icon: '🤍', desc: 'Элегантные мраморные текстуры' },
                    { value: 'Под дерево', icon: '🤎', desc: 'Натуральные древесные рисунки' },
                    { value: 'Под бетон', icon: '🩶', desc: 'Современный индустриальный стиль' },
                    { value: 'Под камень', icon: '🪨', desc: 'Природные каменные фактуры' },
                    { value: 'Однотонная', icon: '⬜', desc: 'Классические цвета' },
                    { value: 'Пока не определился', icon: '❓', desc: 'Нужна консультация' }
                  ].map((tile) => (
                    <button
                      key={tile.value}
                      onClick={() => setQuizData({...quizData, tileType: tile.value})}
                      className={`w-full p-4 text-left border rounded-lg transition-colors ${
                        quizData.tileType === tile.value 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{tile.icon}</span>
                        <div>
                          <div className="font-semibold">{tile.value}</div>
                          <div className="text-sm text-gray-500">{tile.desc}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {quizStep === 5 && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Где планируете использовать?</h4>
                <div className="space-y-2">
                  {[
                    'Ванная комната',
                    'Кухня',
                    'Гостиная/зал',
                    'Прихожая/коридор',
                    'Спальня',
                    'Балкон/лоджия',
                    'Коммерческое помещение',
                    'Весь объект',
                    'Несколько помещений'
                  ].map((room) => (
                    <button
                      key={room}
                      onClick={() => setQuizData({...quizData, roomType: room})}
                      className={`w-full p-3 text-left border rounded-lg transition-colors ${
                        quizData.roomType === room 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {room}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {quizStep === 6 && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Планируемый бюджет (необязательно)</h4>
                <div className="space-y-2">
                  {getRoleBasedOptions().budgets.map((budget) => (
                    <button
                      key={budget}
                      onClick={() => setQuizData({...quizData, budget: budget})}
                      className={`w-full p-3 text-left border rounded-lg transition-colors ${
                        quizData.budget === budget 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {budget}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {quizStep === 7 && (
              <div>
                <h4 className="text-lg font-semibold mb-4">Когда планируете покупку?</h4>
                <div className="space-y-2 mb-4">
                  {['В течение недели', 'В течение месяца', 'В течение 3 месяцев', 'Пока изучаю варианты'].map((timeline) => (
                    <button
                      key={timeline}
                      onClick={() => setQuizData({...quizData, timeline: timeline})}
                      className={`w-full p-3 text-left border rounded-lg transition-colors ${
                        quizData.timeline === timeline 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      {timeline}
                    </button>
                  ))}
                </div>
                <textarea 
                  value={quizData.additionalInfo}
                  onChange={(e) => setQuizData({...quizData, additionalInfo: e.target.value})}
                  placeholder="Дополнительные пожелания или вопросы (необязательно)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 text-sm resize-none"
                  rows={3}
                />
              </div>
            )}

            <div className="flex gap-3 mt-6">
              {quizStep > 1 && (
                <button 
                  onClick={prevStep}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Назад
                </button>
              )}
              <button 
                onClick={nextStep}
                disabled={
                  (quizStep === 1 && !quizData.role) ||
                  (quizStep === 2 && !quizData.projectType) ||
                  (quizStep === 3 && !quizData.area) ||
                  (quizStep === 4 && !quizData.tileType) ||
                  (quizStep === 5 && !quizData.roomType) ||
                  (quizStep === 7 && !quizData.timeline)
                }
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {quizStep === 7 ? 'Отправить в WhatsApp' : 'Далее'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1. ЗАХВАТ ВНИМАНИЯ - Hero Section */}
      <section 
        className="relative min-h-screen bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url("https://public.readdy.ai/ai/img_res/0c0cfa934e39113600d0189901fd647d.jpg")'
        }}
      >
        <div className="container mx-auto px-6 text-white w-full">
          <div className="max-w-4xl">
            <div className="bg-red-600 text-white px-4 py-2 rounded-lg inline-block mb-4 font-bold">
              🏆 ДИСТРИБЬЮТОР №1 В КОСТАНАЙСКОЙ ОБЛАСТИ
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Керамическая плитка премиум-класса от крупнейшего дистрибьютора
            </h1>
            <p className="text-xl md:text-2xl mb-6 text-gray-200">
              Официальный представитель заводов Terramic, Wan SHENG, MinTao. Более 25 000 м² в наличии на складе.
            </p>
            
            {/* Ключевые преимущества */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <i className="ri-store-3-line text-2xl text-green-400 mr-3"></i>
                  <h3 className="font-semibold">Крупнейший склад</h3>
                </div>
                <p className="text-sm text-gray-200">25 000+ м² керамической плитки в наличии</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <i className="ri-award-line text-2xl text-green-400 mr-3"></i>
                  <h3 className="font-semibold">Официальный дистрибьютор</h3>
                </div>
                <p className="text-sm text-gray-200">Прямые поставки от 3 ведущих заводов</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <i className="ri-truck-line text-2xl text-green-400 mr-3"></i>
                  <h3 className="font-semibold">Доставка по области</h3>
                </div>
                <p className="text-sm text-gray-200">Костанай, Рудный, Лисаковск, Аркалык</p>
              </div>
            </div>

            {/* Первый призыв к действию */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => openDirectWhatsApp('Здравствуйте! Хочу получить консультацию по керамической плитке от крупнейшего дистрибьютора.')}
                className="bg-green-600 text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Получить консультацию
              </button>
              <button 
                onClick={openQuiz}
                className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calculator-line mr-2"></i>
                Рассчитать стоимость
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 2. МОТИВАЦИЯ К ПЕРВОМУ КОНТАКТУ */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-6 text-center">
          <div className="bg-blue-600 text-white px-6 py-3 rounded-lg inline-block mb-6 font-bold text-lg">
            🥇 ДИСТРИБЬЮТОР №1 • 25 000+ М² В НАЛИЧИИ
          </div>
          <h2 className="text-3xl font-bold text-green-700 mb-4">Получите расчёт от крупнейшего поставщика за 5 минут</h2>
          <p className="text-xl text-gray-600 mb-8">Прямые поставки от заводов Terramic, Wan SHENG, MinTao — без посредников и переплат</p>
          
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-message-3-line text-2xl text-green-600"></i>
                </div>
                <h3 className="font-semibold mb-2">1. Напишите</h3>
                <p className="text-sm text-gray-600">Опишите ваш проект в WhatsApp</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-calculator-line text-2xl text-green-600"></i>
                </div>
                <h3 className="font-semibold mb-2">2. Получите расчёт</h3>
                <p className="text-sm text-gray-600">Цены напрямую от завода</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-truck-line text-2xl text-green-600"></i>
                </div>
                <h3 className="font-semibold mb-2">3. Получите товар</h3>
                <p className="text-sm text-gray-600">Со склада 25 000+ м²</p>
              </div>
            </div>
            
            <button 
              onClick={() => openDirectWhatsApp('Здравствуйте! Хочу получить расчёт стоимости керамической плитки от крупнейшего дистрибьютора.')}
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
            >
              <i className="ri-whatsapp-line mr-2 text-xl"></i>
              Написать в WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* 3. ДЕМОНСТРАЦИЯ КОНКУРЕНТНЫХ ПРЕИМУЩЕСТВ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="bg-blue-600 text-white px-6 py-3 rounded-lg inline-block mb-6 font-bold text-lg">
              🏭 ПРЯМЫЕ ПОСТАВКИ ОТ ВЕДУЩЕГО ЗАВОДА
            </div>
            <h2 className="text-4xl font-bold mb-4">Почему керамическая плитка лучше других материалов</h2>
            <p className="text-xl text-gray-600">Работаем напрямую с крупнейшим заводом — без посредников и переплат</p>
          </div>
          
          {/* Сравнительная таблица */}
          <div className="overflow-x-auto mb-12">
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden min-w-[800px]">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                    <th className="text-left p-6 font-bold text-lg">Материал</th>
                    <th className="text-center p-6 font-bold text-lg">Срок службы</th>
                    <th className="text-center p-6 font-bold text-lg">Влагостойкость</th>
                    <th className="text-center p-6 font-bold text-lg">Экологичность</th>
                    <th className="text-center p-6 font-bold text-lg">Уход</th>
                    <th className="text-center p-6 font-bold text-lg">Цена за м²</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    <td className="p-6">
                      <div className="flex items-center">
                        <i className="ri-star-fill text-yellow-500 text-2xl mr-3"></i>
                        <span className="text-lg font-bold text-green-800">Керамическая плитка</span>
                      </div>
                    </td>
                    <td className="text-center p-6 font-bold text-green-700">25+ лет</td>
                    <td className="text-center p-6 font-bold text-green-700">100%</td>
                    <td className="text-center p-6 font-bold text-green-700">Класс E0</td>
                    <td className="text-center p-6 font-bold text-green-700">Минимальный</td>
                    <td className="text-center p-6 font-bold text-green-700">от 1200 ₸/м²</td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-6">Обычная плитка</td>
                    <td className="text-center p-6">10-15 лет</td>
                    <td className="text-center p-6">Частично</td>
                    <td className="text-center p-6">Средний</td>
                    <td className="text-center p-6">Средний</td>
                    <td className="text-center p-6">от 800 ₸/м²</td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-6">Обои</td>
                    <td className="text-center p-6">3-5 лет</td>
                    <td className="text-center p-6">Нет</td>
                    <td className="text-center p-6">Низкий</td>
                    <td className="text-center p-6">Сложный</td>
                    <td className="text-center p-6">от 500 ₸</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-6">Краска</td>
                    <td className="text-center p-6">2-3 года</td>
                    <td className="text-center p-6">Частично</td>
                    <td className="text-center p-6">Средний</td>
                    <td className="text-center p-6">Высокий</td>
                    <td className="text-center p-6">от 300 ₸</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-center">
            <button 
              onClick={() => openDirectWhatsApp('Хочу узнать больше о преимуществах керамической плитки от ведущего завода.')}
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Узнать больше о преимуществах
            </button>
          </div>
        </div>
      </section>

      {/* 4. РАСШИРЕНИЕ СЦЕНАРИЕВ ИСПОЛЬЗОВАНИЯ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Где применяется керамическая плитка</h2>
          
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20apartment%20interior%20in%20Kostanay%20Kazakhstan%2C%20ceramic%20tile%20flooring%20in%20residential%20building%2C%20contemporary%20home%20design%20with%20tiles%2C%20Kostanay%20residential%20complex%20interior&width=400&height=300&seq=kostanay-residential&orientation=landscape"
                alt="Жилые помещения"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Жилые помещения</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Ванные комнаты и санузлы</li>
                  <li>• Кухни и столовые</li>
                  <li>• Прихожие и коридоры</li>
                  <li>• Гостиные и спальни</li>
                  <li>• Балконы и лоджии</li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20office%20building%20interior%20in%20Kostanay%20Kazakhstan%2C%20commercial%20space%20with%20ceramic%20wall%20tiles%2C%20business%20center%20lobby%20with%20professional%20tile%20installation%2C%20Kostanay%20commercial%20architecture&width=400&height=300&seq=kostanay-commercial&orientation=landscape"
                alt="Коммерческие помещения"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Коммерческие объекты</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Офисы и бизнес-центры</li>
                  <li>• Магазины и торговые центры</li>
                  <li>• Рестораны и кафе</li>
                  <li>• Гостиницы и отели</li>
                  <li>• Салоны красоты и СПА</li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20hospital%20interior%20in%20Kostanay%20Kazakhstan%2C%20medical%20facility%20with%20hygienic%20ceramic%20tiles%2C%20healthcare%20building%20in%20Kostanay%20city%2C%20clean%20medical%20environment%20with%20professional%20tiles&width=400&height=300&seq=kostanay-medical&orientation=landscape"
                alt="Специальные помещения"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Специальные помещения</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Медицинские учреждения</li>
                  <li>• Образовательные учреждения</li>
                  <li>• Спортивные комплексы</li>
                  <li>• Производственные помещения</li>
                  <li>• Общественные здания</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-semibold mb-4">
              <i className="ri-lightbulb-line mr-2"></i>
              Нестандартные решения
            </h3>
            <p className="text-lg mb-6">Керамическая плитка подходит даже для бассейнов, саун, промышленных кухонь и лабораторий благодаря своей универсальности</p>
            <button 
              onClick={() => openDirectWhatsApp('Хочу обсудить нестандартное применение керамической плитки для моего проекта.')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Обсудить ваш проект
            </button>
          </div>
        </div>
      </section>

      {/* 5. КАЛЬКУЛЯТОР */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Калькулятор стоимости</h2>
            <p className="text-xl text-gray-600 mb-8">Узнайте примерную стоимость за 30 секунд</p>
            
            <div className="bg-gray-50 rounded-xl p-8 mb-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Быстрый расчёт</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Площадь (м²)</label>
                      <input 
                        type="number" 
                        placeholder="Например: 25"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Тип плитки</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 pr-8">
                        <option>Под дерево</option>
                        <option>Под мрамор</option>
                        <option>Под бетон</option>
                        <option>Под камень</option>
                      </select>
                    </div>
                    <button 
                      onClick={openQuiz}
                      className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Получить точный расчёт
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-600 text-white rounded-lg p-6">
              <h3 className="text-xl font-bold mb-2">Хотите точный расчёт?</h3>
              <p className="mb-4">Пройдите короткий квиз — учтём все особенности вашего проекта</p>
              <button 
                onClick={openQuiz}
                className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Пройти квиз (2 минуты)
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Часто задаваемые вопросы</h2>
          <div className="max-w-4xl mx-auto space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm">
                <button 
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50 cursor-pointer"
                  onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                >
                  <span className="text-lg font-semibold">{faq.question}</span>
                  <i className={`ri-arrow-down-s-line text-xl transition-transform ${faqOpen === index ? 'rotate-180' : ''}`}></i>
                </button>
                {faqOpen === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-xl text-gray-600 mb-6">Не нашли ответ на свой вопрос?</p>
            <button 
              onClick={() => openDirectWhatsApp('У меня есть вопрос по керамической плитке, который не освещён в FAQ.')}
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Задать вопрос в WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* 7. ТАЙМЕР */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <i className="ri-time-line mr-3"></i>
              Скидка 5% при заказе до конца недели
            </h2>
            <p className="text-xl text-red-100 mb-6">Успейте оформить заказ и сэкономить!</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
            <h3 className="text-2xl font-semibold mb-4">Осталось:</h3>
            <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-3xl font-bold">{timeLeft.days}</div>
                <div className="text-sm text-red-100">дней</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-3xl font-bold">{timeLeft.hours}</div>
                <div className="text-sm text-red-100">часов</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-3xl font-bold">{timeLeft.minutes}</div>
                <div className="text-sm text-red-100">минут</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-3xl font-bold">{timeLeft.seconds}</div>
                <div className="text-sm text-red-100">секунд</div>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4">Условия акции:</h3>
            <div className="grid md:grid-cols-3 gap-4 text-left">
              <div className="flex items-start">
                <i className="ri-check-line text-2xl text-green-300 mr-3 mt-1"></i>
                <div>
                  <div className="font-medium">Скидка 5%</div>
                  <div className="text-red-100 text-sm">На все виды керамической плитки</div>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-line text-2xl text-green-300 mr-3 mt-1"></i>
                <div>
                  <div className="font-medium">Заполнить квиз</div>
                  <div className="text-red-100 text-sm">И отправить заявку сейчас</div>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-line text-2xl text-green-300 mr-3 mt-1"></i>
                <div>
                  <div className="font-medium">Быстрое оформление</div>
                  <div className="text-red-100 text-sm">Заказ в течение недели</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={openQuiz}
              className="bg-yellow-400 text-red-800 px-12 py-4 rounded-lg text-xl font-bold hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center"
            >
              <i className="ri-gift-line mr-3"></i>
              Получить скидку 5%
            </button>
            <button 
              onClick={() => openDirectWhatsApp('Хочу воспользоваться скидкой 5% на керамической плитке!')}
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              Написать в WhatsApp
            </button>
          </div>
          
          <p className="text-red-100 text-sm mt-4">* Акция не суммируется с другими скидками и предложениями</p>
        </div>
      </section>

      {/* 8. ДОПОЛНИТЕЛЬНЫЕ ЭЛЕМЕНТЫ ДОВЕРИЯ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          {/* Отзывы */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Отзывы наших постоянных клиентов</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <img 
                    alt="Марат Касымов - Директор строительной компании" 
                    className="w-16 h-16 rounded-full object-cover object-top mr-4" 
                    src="https://readdy.ai/api/search-image?query=Professional%20Kazakh%20businessman%20construction%20company%20director%20in%20Kostanay%20Kazakhstan%2C%20confident%20portrait%20wearing%20suit%2C%20successful%20entrepreneur%20from%20Kostanay%20region&width=80&height=80&seq=kostanay-businessman&orientation=squarish"
                  />
                  <div>
                    <h3 className="font-semibold text-lg">Марат К.</h3>
                    <p className="text-gray-600 text-sm">Директор строительной компании</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                </div>
                <p className="text-gray-700">"Работаем с Muslim Ceramica уже 3 года. Прямые поставки от завода, отличные оптовые цены. Всегда есть нужный объем в наличии."</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <img 
                    alt="Анна Петрова - Дизайнер интерьеров" 
                    className="w-16 h-16 rounded-full object-cover object-top mr-4" 
                    src="https://readdy.ai/api/search-image?query=Professional%20female%20interior%20designer%20in%20Kostanay%20Kazakhstan%2C%20confident%20portrait%20in%20modern%20design%20studio%2C%20creative%20workspace%20in%20Kostanay%2C%20stylish%20businesswoman%20architect&width=80&height=80&seq=kostanay-designer&orientation=squarish"
                  />
                  <div>
                    <h3 className="font-semibold text-lg">Анна П.</h3>
                    <p className="text-gray-600 text-sm">Дизайнер интерьеров</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                </div>
                <p className="text-gray-700">"Огромный выбор в шоуруме! Клиенты всегда находят то, что им нужно. Качество плитки отличное, условия адекватные. Очень довольна сотрудничеством."</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <img 
                    alt="Дамир Нуржанов - Застройщик" 
                    className="w-16 h-16 rounded-full object-cover object-top mr-4" 
                    src="https://readdy.ai/api/search-image?query=Professional%20Kazakh%20property%20developer%20in%20Kostanay%20Kazakhstan%2C%20confident%20portrait%20at%20construction%20site%2C%20successful%20real%20estate%20developer%20from%20Kostanay%20region%20wearing%20hard%20hat&width=80&height=80&seq=kostanay-developer&orientation=squarish"
                  />
                  <div>
                    <h3 className="font-semibold text-lg">Дамир Н.</h3>
                    <p className="text-gray-600 text-sm">Застройщик</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                </div>
                <p className="text-gray-700">"Поставили плитку на весь жилой комплекс - большой объем. Сроки соблюдены, качество на высоте. Работать с профессионалами - одно удовольствие!"</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
